import { ApiInstance, scopedTo } from '@/lib/api'
import { cursorParams, type Paginated } from '@/lib/pagination'
import type { Labeled } from '@/lib/tone'

export const OLX_PUBLICATION_TYPES = ['STANDARD', 'PREMIUM', 'SUPER_PREMIUM', 'PREMIERE_1', 'PREMIERE_2', 'TRIPLE'] as const

export type OlxPublicationType = (typeof OLX_PUBLICATION_TYPES)[number]

export const OLX_PUBLICATION_TYPE_LABELS: Record<OlxPublicationType, string> = {
  STANDARD: 'Padrão',
  PREMIUM: 'Destaque',
  SUPER_PREMIUM: 'Super destaque',
  PREMIERE_1: 'Destaque Exclusivo',
  PREMIERE_2: 'Destaque Superior',
  TRIPLE: 'Triplo'
}

export const OLX_ZAP_PLUS_ONLY: readonly OlxPublicationType[] = ['PREMIERE_1', 'PREMIERE_2', 'TRIPLE']

export type OlxListingIssue =
  | 'not_available'
  | 'title_too_short'
  | 'description_too_short'
  | 'missing_purpose'
  | 'missing_sale_price'
  | 'missing_rent_price'
  | 'missing_postal_code'
  | 'missing_state'
  | 'missing_city'
  | 'missing_neighborhood'
  | 'missing_area'
  | 'missing_bedrooms'
  | 'missing_bathrooms'
  | 'missing_images'

export type OlxListingWarning = 'few_images' | 'title_too_long' | 'description_too_long'

const ISSUE_LABELS: Record<OlxListingIssue, string> = {
  not_available: 'O imóvel não está disponível',
  title_too_short: 'Título com menos de 10 caracteres',
  description_too_short: 'Descrição com menos de 50 caracteres',
  missing_purpose: 'Falta a finalidade (venda ou aluguel)',
  missing_sale_price: 'Falta o valor de venda',
  missing_rent_price: 'Falta o valor do aluguel',
  missing_postal_code: 'Falta o CEP',
  missing_state: 'Falta o estado',
  missing_city: 'Falta a cidade',
  missing_neighborhood: 'Falta o bairro',
  missing_area: 'Falta a área',
  missing_bedrooms: 'Falta o número de quartos',
  missing_bathrooms: 'Falta o número de banheiros',
  missing_images: 'Nenhuma foto'
}

const WARNING_LABELS: Record<OlxListingWarning, string> = {
  few_images: 'O portal recomenda pelo menos 5 fotos',
  title_too_long: 'O título passa de 100 caracteres e será cortado',
  description_too_long: 'A descrição passa de 3.000 caracteres e será cortada'
}

export interface OlxIntegration {
  id: string | null
  enabled: boolean
  publicationType: OlxPublicationType
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  feedUrl: string | null
  updatedAt: string | null
}

export interface OlxIntegrationInput {
  enabled: boolean
  publicationType: OlxPublicationType
  contactName: string
  contactEmail: string
  contactPhone: string
}

export interface OlxListing {
  realStateId: string
  integrationEnabled: boolean
  publicationType: OlxPublicationType | null
  effectivePublicationType: OlxPublicationType
  eligible: boolean
  issues: OlxListingIssue[]
  warnings: OlxListingWarning[]
  lastPublishedAt: string | null
}

export interface OlxLead {
  id: string
  originLeadId: string
  realStateId: string
  customerId: string
  portal: string
  leadType: string | null
  name: string | null
  email: string | null
  phone: string | null
  message: string | null
  createdAt: string
}

export const olxIntegrationQueryKey = (companyId?: string | null) => ['olx-integration', companyId ?? '']
export const olxListingQueryKey = (realStateId: string) => ['olx-listing', realStateId]
export const olxLeadsQueryKey = (companyId?: string | null) => ['olx-leads', companyId ?? '']

export async function fetchOlxIntegration(companyId?: string | null): Promise<OlxIntegration> {
  const { data } = await ApiInstance.get<OlxIntegration>('/olx/integration', scopedTo(companyId))
  return data
}

export async function updateOlxIntegration(input: OlxIntegrationInput, companyId?: string | null): Promise<OlxIntegration> {
  const { data } = await ApiInstance.put<OlxIntegration>('/olx/integration', olxIntegrationPayload(input), scopedTo(companyId))
  return data
}

export async function rotateOlxFeedUrl(companyId?: string | null): Promise<OlxIntegration> {
  const { data } = await ApiInstance.post<OlxIntegration>('/olx/integration/token', {}, scopedTo(companyId))
  return data
}

export async function fetchOlxListing(realStateId: string): Promise<OlxListing> {
  const { data } = await ApiInstance.get<OlxListing>(`/olx/listings/${realStateId}`)
  return data
}

export async function updateOlxListing(realStateId: string, publicationType: OlxPublicationType | null): Promise<OlxListing> {
  const { data } = await ApiInstance.put<OlxListing>(`/olx/listings/${realStateId}`, { publicationType })
  return data
}

export async function fetchOlxLeads(companyId?: string | null, perPage = 20): Promise<Paginated<OlxLead>> {
  const { data } = await ApiInstance.get<Paginated<OlxLead>>('/olx/leads', { ...scopedTo(companyId), params: cursorParams(null, perPage) })
  return data
}

export const olxPublicationTypeLabel = (type?: string | null): string =>
  (type && OLX_PUBLICATION_TYPE_LABELS[type as OlxPublicationType]) || OLX_PUBLICATION_TYPE_LABELS.STANDARD

export const olxIssueLabel = (issue: string): string => ISSUE_LABELS[issue as OlxListingIssue] ?? issue

export const olxWarningLabel = (warning: string): string => WARNING_LABELS[warning as OlxListingWarning] ?? warning

export type OlxDraft = OlxIntegrationInput

export interface OlxContactDefaults {
  name?: string | null
  emails?: { email: string }[] | null
  phones?: { phone: string }[] | null
}

export function olxDraftOf(integration: OlxIntegration, company?: OlxContactDefaults | null): OlxDraft {
  const saved = integration.id !== null
  return {
    enabled: integration.enabled,
    publicationType: integration.publicationType ?? 'STANDARD',
    contactName: integration.contactName ?? (saved ? '' : (company?.name ?? '')),
    contactEmail: integration.contactEmail ?? (saved ? '' : (company?.emails?.[0]?.email ?? '')),
    contactPhone: integration.contactPhone ?? (saved ? '' : (company?.phones?.[0]?.phone ?? ''))
  }
}

export function olxIntegrationPayload(input: OlxIntegrationInput) {
  return {
    enabled: input.enabled,
    publicationType: input.publicationType,
    contactName: input.contactName.trim() || null,
    contactEmail: input.contactEmail.trim() || null,
    contactPhone: input.contactPhone.trim() || null
  }
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function olxDraftError(draft: OlxDraft): string | null {
  const email = draft.contactEmail.trim()
  if (email && !EMAIL_PATTERN.test(email)) return 'Informe um e-mail de contato válido'
  if (draft.enabled && (!draft.contactName.trim() || !email)) return 'Informe o nome e o e-mail do contato para ativar a integração'
  return null
}

export function olxListingStatus(listing?: OlxListing | null): Labeled & { summary: string } {
  if (!listing) return { label: 'Carregando', tone: 'neutral', summary: '' }
  if (!listing.integrationEnabled)
    return { label: 'Desligada', tone: 'neutral', summary: 'A integração com o Grupo OLX está desligada nesta imobiliária.' }
  if (!listing.eligible) {
    const blocking = listing.issues.filter(issue => issue !== 'not_available')
    return listing.issues.includes('not_available') && blocking.length === 0
      ? { label: 'Fora do feed', tone: 'neutral', summary: 'Só imóveis disponíveis vão para o feed do Grupo OLX.' }
      : { label: 'Fora do feed', tone: 'warning', summary: 'Complete o cadastro para o imóvel ir ao ZAP, Viva Real e OLX.' }
  }
  if (!listing.lastPublishedAt) return { label: 'Aguardando portal', tone: 'info', summary: 'Entra no feed na próxima leitura do Grupo OLX.' }
  return { label: 'No feed', tone: 'success', summary: 'Incluído no feed lido pelo Grupo OLX (ZAP e Viva Real em até ~4h, OLX em até ~24h).' }
}

export function olxLeadChannelLabel(leadType?: string | null): string {
  if (!leadType) return 'Portal'
  const normalized = leadType.toUpperCase()
  if (normalized.includes('WHATSAPP')) return 'WhatsApp'
  if (normalized.includes('PHONE') || normalized.includes('CALL')) return 'Telefone'
  if (normalized.includes('CHAT')) return 'Chat'
  if (normalized.includes('VISIT')) return 'Pedido de visita'
  if (normalized.includes('FORM') || normalized.includes('CONTACT') || normalized.includes('EMAIL')) return 'Formulário'
  return leadType
}
